import '../Analytics/Analytics.scss';
import { useCallback, useEffect, useState } from 'react';
import SideBar from '../../components/SideBar/SideBar';
import TopNav from '../../components/TopNav/TopNav';
import api from '../../services/api';
import Skeleton from '../../components/Skeleton/Skeleton';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { colors } from '../../styles/tokens';

// EngineStats: admin-only dashboard for the engagement decision engine.
// Sources:
//   - GET /admin/engine-stats/overview?days=N
// The endpoint returns runs bucketed by Miami-local night so the chart maps
// to real engine nights. Today's row in the rendered output is yesterday's
// engine activity (engine runs 9pm-1am Miami; this page is usually loaded
// the following morning).

const FORMAT_DATE = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return `${d.getMonth() + 1}/${d.getDate()}`;
};

const FORMAT_TIMESTAMP = (ts) => {
  if (!ts) return 'never';
  const d = new Date(ts);
  return d.toLocaleString();
};

const EngineStats = () => {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(14);
  const [cronLog, setCronLog] = useState([]);
  const [cronLogLoading, setCronLogLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/engine-stats/overview?days=${days}`);
      setOverview(res.data);
    } catch (error) {
      console.error('Error fetching engine stats:', error);
    } finally {
      setLoading(false);
    }
  }, [days]);

  const fetchCronLog = useCallback(async () => {
    setCronLogLoading(true);
    try {
      const res = await api.get('/admin/engine-stats/standalone-cron-log?limit=100');
      setCronLog(res.data.rows || []);
    } catch (error) {
      console.error('Error fetching standalone cron log:', error);
    } finally {
      setCronLogLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchCronLog();
  }, [fetchCronLog]);

  // Reverse so the chart x-axis runs oldest -> newest left to right; the
  // backend returns newest-first for the table view below.
  const chartData = (overview?.by_night || []).slice().reverse().map((n) => ({
    night: FORMAT_DATE(n.night),
    delivered: n.delivered,
    push_failed: n.push_failed,
    message_gen_failed: n.message_gen_failed,
    user_errors: n.user_errors,
    notifications_disabled: n.skipped.notifications_disabled,
    daily_cap: n.skipped.daily_cap,
    recency_floor: n.skipped.recency_floor,
    open_rate_backoff: n.skipped.open_rate_backoff,
    runs: n.runs,
  }));

  const latest = overview?.latest_run;
  const triggers = overview?.triggers || [];

  return (
    <main>
      <TopNav />
      <div className="main-container">
        <SideBar />
        <article className="page-content">
          <section className="analytics">
            <div className="analytics__header">
              <div>
                <h1 className="analytics__title">Engagement Engine</h1>
                <p className="analytics__subtitle">
                  How is the engagement-driven push engine performing?
                </p>
              </div>
              <div className="analytics__period">
                {[7, 14, 30, 90].map((d) => (
                  <button
                    key={d}
                    type="button"
                    className={days === d ? 'active' : ''}
                    onClick={() => setDays(d)}>
                    {d}d
                  </button>
                ))}
              </div>
            </div>

            {/* Latest-run summary */}
            <div className="analytics__stats">
              <div className="analytics__stat-card">
                <h3>Last Run</h3>
                <p style={{ fontSize: 16 }}>{FORMAT_TIMESTAMP(latest?.run_started_at)}</p>
                <span>Most recent engine cycle</span>
              </div>
              <div className="analytics__stat-card">
                <h3>Delivered (Last Run)</h3>
                <p>{latest?.delivered ?? 0}</p>
                <span>Pushes sent this run</span>
              </div>
              <div className="analytics__stat-card">
                <h3>Users Loaded</h3>
                <p>{latest?.users_loaded ?? 0}</p>
                <span>Eligible cohort size</span>
              </div>
              <div className="analytics__stat-card">
                <h3>Last Run Status</h3>
                <p style={{ color: latest?.errored ? colors.danger : colors.success }}>
                  {latest?.errored ? 'ERRORED' : 'OK'}
                </p>
                <span>{latest?.errored ? 'Check Sentry' : 'No errors'}</span>
              </div>
            </div>

            {/* Delivered per night */}
            <div className="analytics__chart-section" style={{ marginBottom: 16 }}>
              <h3>Pushes delivered per night</h3>
              {loading ? (
                <Skeleton height={260} />
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis dataKey="night" stroke="#888" />
                    <YAxis stroke="#888" allowDecimals={false} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#111', border: '1px solid #333' }}
                    />
                    <Line
                      type="monotone"
                      dataKey="delivered"
                      stroke={colors.gradientStart}
                      strokeWidth={2}
                      dot
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Skip-reason stacked area */}
            <div className="analytics__chart-section" style={{ marginBottom: 16 }}>
              <h3>Why users were skipped (stacked)</h3>
              {loading ? (
                <Skeleton height={260} />
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <AreaChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis dataKey="night" stroke="#888" />
                    <YAxis stroke="#888" allowDecimals={false} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#111', border: '1px solid #333' }}
                    />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="notifications_disabled"
                      stackId="1"
                      stroke="#888"
                      fill="#444"
                    />
                    <Area
                      type="monotone"
                      dataKey="daily_cap"
                      stackId="1"
                      stroke="#f5a623"
                      fill="#f5a623"
                    />
                    <Area
                      type="monotone"
                      dataKey="recency_floor"
                      stackId="1"
                      stroke="#7229F5"
                      fill="#7229F5"
                    />
                    <Area
                      type="monotone"
                      dataKey="open_rate_backoff"
                      stackId="1"
                      stroke="#d0021b"
                      fill="#d0021b"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Triggers table */}
            <div className="analytics__chart-section">
              <h3>Trigger health (last 30d)</h3>
              {loading ? (
                <Skeleton height={200} />
              ) : triggers.length === 0 ? (
                <p style={{ color: '#888' }}>No trigger health data yet.</p>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', color: '#fff' }}>
                  <thead>
                    <tr>
                      <th>Trigger</th>
                      <th>Sends 30d</th>
                      <th>Taps 30d</th>
                      <th>Tap rate</th>
                      <th>Status</th>
                      <th>Pause reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {triggers.map((t) => (
                      <tr key={t.trigger_type}>
                        <td>{t.trigger_type}</td>
                        <td>{t.sends_30d}</td>
                        <td>{t.taps_30d}</td>
                        <td>{(t.tap_rate_30d * 100).toFixed(1)}%</td>
                        <td
                          style={{
                            color: t.health_status === 'PAUSED' ? colors.danger : colors.success,
                          }}>
                          {t.health_status}
                        </td>
                        <td style={{ color: '#888' }}>{t.pause_reason || ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Standalone-cron send log (profile reminder + offer reminder).
                These run independent of the engine above, so they're logged
                here separately: who got what, when. */}
            <div className="analytics__chart-section">
              <h3>Profile &amp; offer reminders (standalone crons)</h3>
              {cronLogLoading ? (
                <Skeleton height={200} />
              ) : cronLog.length === 0 ? (
                <p style={{ color: '#888' }}>No sends logged yet.</p>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', color: '#fff' }}>
                  <thead>
                    <tr>
                      <th>Sent</th>
                      <th>User</th>
                      <th>Trigger</th>
                      <th>Status</th>
                      <th>Opened</th>
                      <th>Message</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cronLog.map((row) => (
                      <tr key={row._id}>
                        <td style={{ whiteSpace: 'nowrap' }}>{FORMAT_TIMESTAMP(row.created_at)}</td>
                        <td>
                          {row.user
                            ? `${row.user.firstName || ''} ${row.user.lastName || ''}`.trim() ||
                              row.user.email
                            : 'unknown'}
                        </td>
                        <td>{row.trigger_type}</td>
                        <td
                          style={{
                            color: row.delivery_status === 'FAILED' ? colors.danger : colors.success,
                          }}>
                          {row.delivery_status}
                        </td>
                        <td style={{ color: row.opened ? colors.success : '#888' }}>
                          {row.delivery_status !== 'DELIVERED'
                            ? '—'
                            : row.opened
                              ? `Yes, ${FORMAT_TIMESTAMP(row.opened_at)}`
                              : 'Not yet'}
                        </td>
                        <td style={{ color: '#ccc' }}>{row.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </section>
        </article>
      </div>
    </main>
  );
};

export default EngineStats;
